// SIMS-8948: the backend-1 client import is gone — with the PPC card calls
// removed, every call in this service now goes to backend-2.
import { instanceBo } from "../utils/axios.create";
import {
	BeneficiaryItem,
	BeneficiaryItemSearched,
	BeneficiaryPayload,
	BeneficiaryResponse,
	BeneficiaryVisitData,
	Program,
	Provider,
	ProviderSite,
	IWeekStatus,
	ConsentFormData,
	Consent,
	ConsentPayload,
	ConsentUrlResponse,
	BeneficiaryEnrollmentPeriodsResponse,
	BeneficiaryIncentiveHistoryResponse,
} from "../domain/beneficiary.interface";
import dayjs from "dayjs";
import { TreeItem } from "../domain/common.interface";

/** Get beneficiary list from backend 2 with program+OU combination filtering */
const findAllV2 = async ({
	providerSite,
	programId,
	providerId,
	userId,
	status,
	search,
	pageNumber,
	pageSize,
}: {
	providerSite: string[];
	programId: string;
	providerId: string;
	userId: string;
	status: string[];
	search: string;
	pageNumber: number;
	pageSize: number;
}): Promise<{
	data: BeneficiaryResponse[];
	total: number;
	totalCard: number;
	totalActive: number;
	totalDisenroll: number;
	totalCompleted: number;
	totalPaused: number;
}> => {
	const params = new URLSearchParams();

	if (userId) params.append("userId", userId);
	if (programId && programId !== "all") params.append("programIds", programId);
	if (providerSite.length > 0)
		params.append("orgUnitIds", providerSite.join(","));
	if (providerId && providerId !== "all")
		params.append("assignedUserId", providerId);
	if (status.length > 0) params.append("status", status.join(","));
	if (search) params.append("search", search);
	params.append("pageNumber", String(pageNumber));
	params.append("pageSize", String(pageSize));

	return instanceBo
		.get(`beneficiaries/list?${params.toString()}`)
		.then((res) => {
			const body = res.data.body;
			return {
				data: body.data,
				total: body.total,
				totalCard: body.totalCard,
				totalActive: body.totalActive,
				totalCompleted: body.totalCompleted,
				totalDisenroll: body.totalDisenroll,
				totalPaused: body.totalPaused || 0,
			};
		})
		.catch((err) => Promise.reject(err?.response?.data || err));
};

/**
 * Get beneficiary info from backend 2 with cups/analytes data populated
 */
const findByIdV2 = async (
	beneficiaryId: string,
	enrollmentId?: string,
): Promise<BeneficiaryResponse> => {
	let enrollmentIdParam = "";

	if (enrollmentId && enrollmentId.trim() !== "") {
		enrollmentIdParam = `?enrollmentId=${enrollmentId}`;
	}

	return instanceBo
		.get(`beneficiaries/${beneficiaryId}${enrollmentIdParam}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/**
 * Register a participant and enrol them in one call.
 *
 * SIMS-6722: migrated from backend-1 to backend-2. Backend-1 built the enrollment snapshot itself
 * from the `treatmentConfig` sent here and had no segments awareness, so participants registered on
 * a SEGMENTED treatment plan got a snapshot with no `segments` and every segment-aware consumer
 * silently fell back to the template default. Backend-2 composes its own `createEnrollment`, which
 * folds the template segments in (SIMS-7479).
 *
 * The caller-facing shape is unchanged (`BeneficiaryPayload` in, same object out); the split into
 * `participant` + `enrollment` happens here because backend-2 models them as separate concerns.
 */
const create = async (
	data: BeneficiaryPayload,
): Promise<{
	beneficiaryId: string;
	enrollmentId: string;
	// SIMS-7604: set when the program's budget gate evaluates WARN — the
	// enrollment is created but the Members flow should surface a warning.
	budgetWarning?: boolean;
}> => {
	const {
		// Supplied by instanceBo's baseURL, not the body.
		accountId: _accountId,
		sims,
		userId,
		programId,
		orgUnitId,
		providerId,
		providerSite,
		treatmentPlan,
		treatmentConfigTemplate,
		treatmentConfig,
		consentFile,
		fileType,
		fileName,
		// Re-enroll-only flags; the v1 create ignored them too.
		isReenrol: _isReenrol,
		cmVisitScheduleChanged: _cmVisitScheduleChanged,
		reason: _reason,
		...participant
	} = data;

	return instanceBo
		.post("beneficiaries/with-enrollment", {
			...participant,
			// Backend-2 takes the sims sub-document as flat fields and assembles it itself.
			clientIdNumber: sims?.clientIdNumber,
			medicaidEligibility: sims?.medicaidEligibility,
			cmEligibility: sims?.cmEligibility,
			cmVisitSchedule: sims?.cmVisitSchedule,
			createdBy: userId,
			enrollment: {
				programId,
				orgUnitId,
				providerId,
				providerSite,
				userId,
				...(treatmentPlan && { treatmentPlan }),
				...(treatmentConfigTemplate && { treatmentConfigTemplate }),
				...(treatmentConfig && { treatmentConfig }),
				// Consent belongs to the enrollment, not the participant.
				...(consentFile && { consentFile, fileType, fileName }),
			},
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err?.response?.data || err));
};

/**
 * SIMS-6725: migrated to backend-2 `PUT beneficiaries/{id}/enrollments/{enrollmentId}`
 * (`instanceBo`, whose baseURL already carries `/accounts/:accountId`).
 *
 * The path gains an explicit `enrollments` segment rather than copying backend-1's positional
 * `{beneficiaryId}/{enrollmentId}` — a two-segment positional route would shadow the existing
 * `beneficiaries/{id}/invite` on the backend.
 *
 * `isReenrol` and `cmVisitScheduleChanged` in the payload are NOT hints: they select what happens
 * to the enrollment. Neither set means the enrollment is deliberately untouched, which is what
 * makes it safe for this same call to carry a participant-details-only edit. EditBeneficiary
 * computes both flags; do not drop them.
 *
 * The response was always `{ beneficiaryId, enrollmentId }` despite the old `BeneficiaryItem`
 * annotation; the type now says what actually arrives.
 */
const update = async (
	data: Partial<BeneficiaryPayload>,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<{ beneficiaryId: string; enrollmentId: string }> => {
	return instanceBo
		.put(`beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/**
 * SIMS-6730: migrated from backend-1 `PATCH /beneficiaries/patch/{id}` to backend-2
 * `PATCH beneficiaries/{id}/contact-information` (`instanceBo`, whose baseURL already
 * carries `/accounts/:accountId`).
 *
 * The backend-2 route is deliberately NOT the general participant update used by the
 * Participants page: it disables the email-uniqueness check and the Cognito phone sync,
 * neither of which backend-1 performed. Behaviour on this path is therefore unchanged by
 * the migration.
 *
 * `userId` is mapped to `updatedBy` here rather than at the call sites, so the caller
 * signature stays as it was. The response was always `{ beneficiaryId }` despite the old
 * `BeneficiaryItem` annotation; the type now says what actually arrives.
 */
const patchBeneficiary = async (
	beneficiaryId: string,
	data: Partial<Pick<BeneficiaryPayload, "userId" | "phone" | "email">>,
): Promise<{ beneficiaryId: string }> => {
	const { userId, ...contactInformation } = data;
	return (
		instanceBo
			.patch(`beneficiaries/${beneficiaryId}/contact-information`, {
				...contactInformation,
				updatedBy: userId,
			})
			.then((res) => res.data.body)
			// Guarded: a network-level failure has no `response`, and the unguarded form throws
			// "Cannot read properties of undefined" over the top of the real error. The rest of this
			// file still uses the unguarded form — pre-existing, and not rewritten wholesale inside a
			// migration PR — but code written by this epic uses the safe one.
			.catch((err) => Promise.reject(err.response?.data || err))
	);
};

/**
 * SIMS-6770: migrated to backend-2 (`instanceBo`).
 *
 * NOT the same endpoint as `enrollmentService.reconfirmEligibility`, which the Participants page
 * uses. This one reconfirms in any enrollment status, stamps `updatedBy` and emits the activity +
 * alert events — backend-1's behaviour, which this page keeps (PO decision, 2026-08-15).
 * Same request body and same `{ beneficiaryId, enrollmentId }` response as v1.
 */
const updateReconfirm = async (
	userId: string,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<BeneficiaryItem> => {
	return instanceBo
		.put(
			`beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}/eligibility`,
			{
				userId,
			},
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/** SIMS-6740: migrated to backend-2 (`instanceBo`); same response shape. */
const getConsentForms = async (
	beneficiaryId: string,
): Promise<ConsentFormData[]> => {
	return instanceBo
		.get(`beneficiaries/${beneficiaryId}/consents`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const patchConsentForm = async (
	data: ConsentPayload,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<Consent> => {
	return instanceBo
		.patch(
			`beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}/consent`,
			data,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/**
 * SIMS-6740: migrated to backend-2, and the CONTRACT CHANGED deliberately.
 *
 * backend-1 took the consent's `s3Url` as a query parameter and presigned whatever it was
 * handed — the client effectively chose which object to download. backend-2 takes the
 * enrollment and resolves the object itself, refusing when the enrollment does not belong to
 * the participant.
 *
 * So this no longer passes a URL. The caller names the enrollment, which
 * `ManageConsents` already has from `getConsentForms`.
 */
const getConsentFormUrl = async (
	beneficiaryId: string,
	enrollmentId: string,
): Promise<ConsentUrlResponse> => {
	return instanceBo
		.get(
			`beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}/consent-file`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/** Get programs by IDs from backend 2 */
const findProgramsV2 = async (programIds: string[]): Promise<Program[]> => {
	return instanceBo
		.get(`beneficiaries/programs?ids=${programIds.join(",")}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err?.response?.data || err));
};

const findProviderSites = async (
	ouIds: string[],
): Promise<
	{
		key: string;
		id: string;
		value: string;
		name: string;
		children: TreeItem[];
		groupLabelIds?: string[];
	}[]
> => {
	if (ouIds.length === 0) {
		return [];
	}

	// Use comma-separated values instead of ids[]=value format
	// API Gateway doesn't properly handle duplicate parameter names
	const idsParam = ouIds.join(",");

	return instanceBo
		.get(`/organizations/providers-site?ids=${idsParam}`)
		.then((res) => {
			const providers = res.data.body;

			return providers.map((providerSite: ProviderSite) => {
				return {
					key: providerSite._id,
					id: providerSite._id,
					value: providerSite.name,
					name: providerSite.name,
					groupLabelIds: providerSite.groupLabelIds || [],
				};
			});
		})
		.catch((err) => Promise.reject(err.response.data));
};

/** Get providers by program and OU from backend 2 */
const findProvidersV2 = async (
	programId: string[],
	orgUnitId: string[],
	includeAll: boolean,
): Promise<{ key: string; value: string; roles: string[] }[]> => {
	return instanceBo
		.get(
			`beneficiaries/providers?programIds=${programId.join(",")}&orgUnitIds=${orgUnitId.join(",")}`,
		)
		.then((res) => {
			const providers = res.data.body as Provider[];
			const optionAll = includeAll
				? [{ key: "all", value: "All", roles: [] }]
				: [];

			return [
				...optionAll,
				...providers.map((provider: Provider) => {
					return {
						key: provider._id,
						value: `${provider.firstName} ${provider.lastName}`,
						roles: provider.roles,
					};
				}),
			];
		})
		.catch((err) => Promise.reject(err?.response?.data || err));
};

/**
 * SIMS-5916: the disenroll endpoint was migrated to backend-2 (PR #883). Calls
 * go through `instanceBo` (Option A route) whose baseURL already includes
 * `/accounts/:accountId`. On a pending-approval block the backend responds 400
 * with a top-level body `{ code: "PENDING_APPROVAL_EXISTS", count: N }`; the
 * rejection forwards `err.response.data` so that structured body propagates.
 */
const disenroll = async (
	data: {
		reasons: string;
		notes: string;
		userId: string;
	},
	beneficiaryId: string,
	enrollmentId: string,
): Promise<BeneficiaryItem> => {
	return instanceBo
		.put(`beneficiaries/disenroll/${beneficiaryId}/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data));
};

/**
 * SIMS-6765: migrated to backend-2 (`instanceBo`).
 *
 * 🔴 The returned `enrollmentId` is NOT always the one passed in. A participant disenrolled less
 * than 30 days after their last positive/negative result is REACTIVATED and keeps their
 * enrollment; anyone else — disenrolled longer, completed, or changing program — gets a NEW one.
 * Use the id from the response for anything that follows, not the one sent.
 */
const reenroll = async (
	data: Partial<BeneficiaryPayload>,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<{
	beneficiaryId: string;
	enrollmentId: string;
	// SIMS-7604: WARN-mode flag on the re-enroll path (see `create`).
	budgetWarning?: boolean;
}> => {
	return instanceBo
		.put(
			`beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}/reenroll`,
			data,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const findByName = async (
	name: string,
	userId: string,
): Promise<BeneficiaryItemSearched[]> => {
	// SIMS-8946: migrated to backend-2 (`instanceBo`). Same path, same query
	// parameter names, same response shape — backend-2 projects `_id` and
	// `fullname` and wraps them in BaseResponse, so `res.data.body` is still the
	// array BeneficiarySearchBar renders.
	//
	// The query is now built with URLSearchParams rather than interpolated. The
	// old form sent the raw search box contents straight into the URL, so a
	// participant search for "Smith & Jones" produced a malformed query and a
	// "#" truncated it. Pre-existing, and fixed here because this is the one
	// line being touched.
	const queryParams = new URLSearchParams({ data: name, userId });

	return instanceBo
		.get(`/beneficiaries/search?${queryParams.toString()}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const findListVisits = async (
	enrollmentId: string,
	rangeWeek: string,
): Promise<BeneficiaryVisitData[]> => {
	const [from, to] = rangeWeek.split("-");
	return instanceBo
		.get(`enrollments/${enrollmentId}/udt-results?from=${from}&to=${to}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getCurrentWeekUdtStatus = async (
	enrollmentId: string,
	behaviorId?: string,
	treatmentConfigName?: string,
): Promise<IWeekStatus> => {
	const params = new URLSearchParams();
	if (behaviorId) {
		params.append("behaviorId", behaviorId);
	}
	if (treatmentConfigName) {
		params.append("treatmentConfigName", treatmentConfigName);
	}
	// Add cache-busting timestamp to prevent 304 Not Modified responses
	params.append("_t", Date.now().toString());

	const queryString = params.toString();
	const url = `/enrollments/${enrollmentId}/current-week${queryString ? `?${queryString}` : ""}`;

	return instanceBo
		.get(url)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/**
 * SIMS-6836: migrated from backend-1 to backend-2 (`instanceBo`).
 *
 * The SHAPE changed with the backend. v1 nested the program —
 * `program: { _id, name, type, multiTreatmentPlan, treatmentPlanChangeAllowed }` — and
 * backend-2 projects it flat as `programId` + `programName`. Only the name is read here,
 * so the label is built from `programName`.
 *
 * Reading `item.program.name` against the v2 payload yields `undefined` rather than an
 * error, which renders as "undefined: 01/01/2026 - ..." in the dropdown with nothing
 * failing — hence the explicit type change below rather than a cast.
 *
 * The flat shape is NOT something backend-2 should be asked to change: the same shared
 * service backs the mobile app's enrollment-periods route.
 */
const getEnrollmentPeriods = async (
	beneficiaryId: string,
): Promise<{ key: string; value: string }[]> => {
	return instanceBo
		.get(`/beneficiaries/${beneficiaryId}/enrollment-periods`)
		.then((res) => {
			const response = res.data.body;

			const beneficiaryEnrollmentPeriods = response.map(
				(item: BeneficiaryEnrollmentPeriodsResponse) => {
					const value = `${item.programName}: ${dayjs(item.startDate).format("MM/DD/YYYY")} - ${item.endDate ? dayjs(item.endDate).format("MM/DD/YYYY") : ""}`;

					return {
						key: item._id,
						value,
					};
				},
			);

			return beneficiaryEnrollmentPeriods;
		})
		.catch((err) => Promise.reject(err.response.data));
};

const findIncentiveHistory = async (
	enrollmentId: string,
	pageNumber: number,
	pageSize: number,
): Promise<BeneficiaryIncentiveHistoryResponse> => {
	// SIMS-6472 / SIMS-7062: migrated to backend-v2 (instanceBo). Backend-v2 keeps
	// the v1 contract — pageNumber is 1-based — and now returns the EXTERNAL
	// redemption `notes`.
	return instanceBo
		.get(
			`enrollments/${enrollmentId}/incentive-history?pageNumber=${pageNumber + 1}&pageSize=${pageSize}`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const putBeneficiaryInvite = async (
	beneficiaryId: string,
	invitedBy: string,
): Promise<{ message: string }> => {
	// backend-2 validates `invitedBy` with @IsMongoId + @IsNotEmpty, so an empty value can only
	// ever 400 — and the coordinator would see a raw validation message they cannot act on.
	// Rejected here, before the request, with something actionable. Guarding in the service
	// rather than at the call site covers every caller, and is testable without rendering a page.
	if (!invitedBy) {
		return Promise.reject({
			message:
				"Could not identify the signed-in user. Please sign in again and retry.",
		});
	}

	return instanceBo
		.put(`beneficiaries/${beneficiaryId}/mobile-access`, { invitedBy })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const updatePayerPlan = async (
	beneficiaryId: string,
	data: {
		payerId: string;
		oldMemberIdNumber: string;
		newMemberIdNumber: string;
		changedBy?: string;
	},
): Promise<{ message: string }> => {
	return instanceBo
		.patch(`beneficiaries/${beneficiaryId}/payer-plans`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getBeneficiaryPayerPlans = (
	beneficiaryId: string,
): Promise<
	{
		payerId: string;
		memberIdNumber: string;
		createdAt: Date;
	}[]
> => {
	return instanceBo
		.get(`beneficiaries/${beneficiaryId}/payer-plans`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

export const beneficiaryService = {
	findAllV2,
	findByIdV2,
	create,
	update,
	patchBeneficiary,
	updateReconfirm,
	getConsentForms,
	patchConsentForm,
	getConsentFormUrl,
	findProgramsV2,
	findProviderSites,
	findProvidersV2,
	disenroll,
	reenroll,
	findByName,
	findListVisits,
	getCurrentWeekUdtStatus,
	getEnrollmentPeriods,
	findIncentiveHistory,
	putBeneficiaryInvite,
	updatePayerPlan,
	getBeneficiaryPayerPlans,
};
